import React, { useEffect, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { openAppConfirm } from "../../components/AppDialogs";

import {
  GetMyUploadsCall,
  UploadMyFileCall,
  DeleteMyUploadCall,
  getPersistedUploads,
  persistUploads,
} from "../../redux/api/uploads.api";

const tabConfig = [
  { value: "all", label: "All", icon: null },
  { value: "image", label: "Images", icon: <ImageIcon fontSize="small" /> },
  { value: "video", label: "Videos", icon: <MovieIcon fontSize="small" /> },
];

function filterByTab(files, tab) {
  if (tab === "all") return files;
  if (tab === "image") return files.filter((f) => f.type?.startsWith("image/"));
  if (tab === "video") return files.filter((f) => f.type?.startsWith("video/"));
  return files;
}

const Uploads = () => {
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const list = await GetMyUploadsCall("user_upload");
      const persisted = getPersistedUploads();
      const byUrl = new Map();
      persisted.forEach((item) => byUrl.set(item.url, item));
      (list || []).forEach((item) => byUrl.set(item.url, item));
      const merged = Array.from(byUrl.values());
      setFiles((prev) => (merged.length > 0 ? merged : (persisted.length > 0 ? persisted : prev)));
    } catch (e) {
      console.error("Error loading uploads", e);
      const persisted = getPersistedUploads();
      if (persisted.length > 0) setFiles(persisted);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const persisted = getPersistedUploads();
    if (persisted.length > 0) setFiles(persisted);
    fetchFiles();
  }, []);

  const handleUploadClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await UploadMyFileCall(file, "user_upload");
      if (url) {
        const item = {
          id: `local-${Date.now()}`,
          url,
          name: file.name,
          alias: file.name,
          type: file.type || (file.name.match(/\.(mp4|webm|mov)$/i) ? "video/mp4" : "image/png"),
        };
        setFiles((prev) => {
          const next = [...prev, item];
          persistUploads(next);
          return next;
        });
        fetchFiles();
      }
    } catch (e) {
      console.error("Upload failed", e);
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const visibleFiles = filterByTab(files, tab);

  return (
    <MDBox pt={1.5} pb={2} px={2}>
      <MDBox mb={2}>
        <MDTypography variant="h5" fontWeight="bold">
          Uploads
        </MDTypography>
        <MDTypography variant="button" color="text">
          Manage your private images and videos for presentations.
        </MDTypography>
      </MDBox>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
          overflow: "hidden",
        }}
      >
        <MDBox
          px={2.5}
          py={2}
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            borderBottom: "1px solid rgba(148,163,184,0.35)",
            bgcolor: "background.paper",
          }}
        >
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleUploadClick}
            startIcon={<UploadIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
            }}
          >
            {uploading ? "Uploading…" : "Upload files"}
          </MDButton>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <Box flexGrow={1} />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                textTransform: "none",
                fontSize: 13,
                fontWeight: 600,
              },
            }}
          >
            {tabConfig.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                label={
                  <Box display="flex" alignItems="center" gap={0.75}>
                    {item.icon}
                    <span>{item.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </MDBox>

        <MDBox
          px={2.5}
          py={2}
          sx={{
            maxHeight: "calc(100vh - 260px)",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={6}
            >
              <CircularProgress size={28} />
            </Box>
          ) : visibleFiles.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                No uploads yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use “Upload files” to add private media.
              </Typography>
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
              gap={1.5}
            >
              {visibleFiles.map((file) => (
                <Box
                  key={file.id || file.url}
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "grey.100",
                    border: "1px solid rgba(148,163,184,0.4)",
                    cursor: "default",
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      bgcolor: "rgba(15,23,42,0.76)",
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(15,23,42,0.95)" },
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!file.id) return;
                      const isLocal = String(file.id).startsWith("local-");
                      if (isLocal) {
                        setFiles((prev) => {
                          const next = prev.filter((f) => f.id !== file.id);
                          persistUploads(next);
                          return next;
                        });
                        return;
                      }
                      const ok = await openAppConfirm({
                        title: "Delete upload?",
                        message: "Delete this upload from your library?",
                        confirmText: "Delete",
                        confirmColor: "error",
                      });
                      if (!ok) return;
                      try {
                        await DeleteMyUploadCall(file.id);
                        setFiles((prev) => {
                          const next = prev.filter((f) => f.id !== file.id);
                          persistUploads(next);
                          return next;
                        });
                      } catch (e) {
                        console.error("Failed to delete upload", e);
                      }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  {file.type?.startsWith("image/") ? (
                    <Box
                      component="img"
                      src={file.url}
                      alt={file.name}
                      sx={{
                        width: "100%",
                        height: 100,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.900",
                        color: "grey.50",
                      }}
                    >
                      <MovieIcon sx={{ fontSize: 32 }} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "auto 6px 6px 6px",
                      bgcolor: "rgba(15,23,42,0.78)",
                      color: "#fff",
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      px: 1.25,
                      py: 0.25,
                      gap: 0.75,
                    }}
                  >
                    {file.type?.startsWith("image/") ? (
                      <ImageIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <MovieIcon sx={{ fontSize: 14 }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 120,
                      }}
                    >
                      {file.alias || file.name}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default Uploads;

